import { getInactiveReason } from './playback-status-store.js';

/** @typedef {'offline' | 'api_unreachable' | 'syncing' | 'outside_schedule' | 'empty_playlist' | 'no_slots_active' | 'hidden'} ConnectionDisplay */

/** @type {{ networkOnline: boolean; apiReachable: boolean; syncDepth: number; reconnectPending: boolean }} */
let state = {
  networkOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  apiReachable: true,
  syncDepth: 0,
  reconnectPending: false,
};

/** @type {Set<(display: ConnectionDisplay) => void>} */
const listeners = new Set();

/** @returns {ConnectionDisplay} */
export function getConnectionDisplay() {
  if (!state.networkOnline) return 'offline';
  if (!state.apiReachable) return 'api_unreachable';
  if (state.syncDepth > 0) return 'syncing';
  const inactive = getInactiveReason();
  if (inactive === 'empty_playlist' || inactive === 'no_slots_active') return 'empty_playlist';
  if (inactive === 'outside_schedule') return 'outside_schedule';
  return 'hidden';
}

function notify() {
  const display = getConnectionDisplay();
  for (const fn of listeners) fn(display);
}

/** Re-evaluate display (e.g. after inactive reason changes). */
export function refreshConnectionDisplay() {
  notify();
}

/** @param {(display: ConnectionDisplay) => void} fn */
export function subscribeConnectionDisplay(fn) {
  listeners.add(fn);
  fn(getConnectionDisplay());
  return () => listeners.delete(fn);
}

export function setNetworkOnline(online) {
  if (state.networkOnline === online) return;
  state.networkOnline = online;
  if (online) {
    state.reconnectPending = true;
    state.apiReachable = true;
  } else {
    state.apiReachable = true;
  }
  notify();
}

/** @returns {boolean} */
export function consumeReconnectPending() {
  const pending = state.reconnectPending;
  state.reconnectPending = false;
  return pending;
}

export function setApiReachable(reachable) {
  if (state.apiReachable === reachable) return;
  state.apiReachable = reachable;
  notify();
}

export function beginSync() {
  state.syncDepth += 1;
  notify();
}

export function endSync() {
  state.syncDepth = Math.max(0, state.syncDepth - 1);
  notify();
}
